import React from 'react';
import { useNavigate } from 'react-router-dom';
import useHttp from '../../hooks/http.hook';
import '../business/Business.css';
import '../product/ProductIndexPage.css';
import CreateBusinessHeader from '../business/components/CreateBusinessHeader';
import CreateFooter from '../business/components/CreateFooter';

function bannerKey(banner) {
  return `${banner?.index}::${banner?.url ?? ''}`;
}

function buildIndexPayload(banners, selectedOrder) {
  const selectedSet = new Set(selectedOrder.map(String));
  const unselectedKeys = banners
    .map(bannerKey)
    .filter((key) => key && !selectedSet.has(String(key)));

  const orderedKeys = [...selectedOrder, ...unselectedKeys];
  const byKey = new Map(banners.map((banner) => [bannerKey(banner), banner]));

  return orderedKeys
    .map((key, index) => {
      const banner = byKey.get(String(key));
      if (!banner?.url) return null;
      return { url: banner.url, index };
    })
    .filter(Boolean);
}

export default function BannerIndexPage() {
  const navigate = useNavigate();
  const { loading, requestWithMeta } = useHttp();

  const [banners, setBanners] = React.useState([]);
  const [bannersLoading, setBannersLoading] = React.useState(true);
  const [selectedOrder, setSelectedOrder] = React.useState([]);
  const [submitError, setSubmitError] = React.useState(null);

  const skeletonItems = React.useMemo(() => Array.from({ length: 4 }, (_, i) => i), []);

  const fetchBanners = React.useCallback(async () => {
    setBannersLoading(true);
    setSubmitError(null);
    try {
      const res = await requestWithMeta('/operator/banners', 'GET');
      if (!res?.ok) {
        setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось загрузить баннеры');
        return;
      }

      const list = Array.isArray(res?.data?.banners) ? res.data.banners : [];
      setBanners(list);
      setSelectedOrder([]);
    } finally {
      setBannersLoading(false);
    }
  }, [requestWithMeta]);

  React.useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  const getOrderNumber = React.useCallback(
    (key) => {
      const idx = selectedOrder.findIndex((id) => String(id) === String(key));
      return idx === -1 ? null : idx + 1;
    },
    [selectedOrder]
  );

  const toggleBanner = React.useCallback((key) => {
    setSelectedOrder((prev) => {
      const idx = prev.findIndex((id) => String(id) === String(key));
      if (idx === -1) return [...prev, key];
      return prev.filter((id) => String(id) !== String(key));
    });
  }, []);

  const selectedCount = selectedOrder.length;
  const isSubmitDisabled = loading || bannersLoading || selectedCount === 0;

  async function handleSubmit() {
    setSubmitError(null);

    const payload = buildIndexPayload(banners, selectedOrder);
    if (payload.length === 0) {
      setSubmitError('Нет баннеров для сохранения');
      return;
    }

    const res = await requestWithMeta('/operator/banners/index', 'PUT', {
      banners: payload,
    });

    if (!res?.ok) {
      setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось сохранить порядок');
      return;
    }

    navigate(-1);
  }

  return (
    <div className="shop-create-root product-index-page-root">
      <div className="shop-create-mobile">
        <CreateBusinessHeader
          title="Порядок баннеров"
          subtitle="Нажимайте на баннеры в нужном порядке. Повторное нажатие снимает номер."
          onBack={() => navigate(-1)}
          backClassName="product-index-page-backButton"
        />

        <main className="shop-create-content">
          {submitError ? (
            <section className="shop-create-section shop-create-sectionError">
              <div className="shop-create-label">Ошибка</div>
              <div className="shop-create-help shop-create-helpTight">{submitError}</div>
            </section>
          ) : null}

          <section className="shop-create-section">
            <div className="product-index-page-listHeader">
              <div className="shop-create-label product-index-page-listTitle">Баннеры</div>
              <div className="shop-create-help shop-create-helpTight">
                {bannersLoading
                  ? 'Загрузка…'
                  : selectedCount > 0
                    ? `Выбрано: ${selectedCount} из ${banners.length}`
                    : `${banners.length} баннеров`}
              </div>
            </div>

            <div className="product-index-page-list">
              {bannersLoading ? (
                skeletonItems.map((item) => (
                  <div key={item} className="product-index-page-item product-index-page-item--skeleton">
                    <div className="shop-create-skeleton product-index-page-skeletonImage" />
                    <div className="shop-create-skeleton shop-create-skeleton--text product-index-page-skeletonName" />
                    <div className="shop-create-skeleton product-index-page-skeletonBadge" />
                  </div>
                ))
              ) : banners.length === 0 ? (
                <div className="shop-create-help">Баннеры не найдены.</div>
              ) : (
                banners.map((banner, i) => {
                  const key = bannerKey(banner);
                  const orderNumber = getOrderNumber(key);
                  const isSelected = orderNumber != null;
                  const name = `Баннер ${Number(banner?.index ?? i) + 1}`;

                  return (
                    <button
                      key={key}
                      type="button"
                      className={`product-index-page-item${isSelected ? ' product-index-page-item--selected' : ''}`}
                      onClick={() => toggleBanner(key)}
                      aria-pressed={isSelected}
                      aria-label={
                        isSelected
                          ? `${name}, порядок ${orderNumber}. Нажмите, чтобы снять`
                          : `${name}. Нажмите, чтобы выбрать`
                      }
                    >
                      <img
                        className="product-index-page-image"
                        src={banner?.url || ''}
                        alt=""
                        loading="lazy"
                      />

                      <div className="product-index-page-main">
                        <div className="product-index-page-name">{name}</div>
                      </div>

                      <div
                        className={`product-index-page-orderBadge${isSelected ? ' product-index-page-orderBadge--filled' : ''}`}
                        aria-hidden="true"
                      >
                        {isSelected ? orderNumber : ''}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </section>
        </main>

        <CreateFooter
          disabled={isSubmitDisabled}
          label={loading ? 'Сохранение…' : 'Сохранить порядок'}
          onCreate={handleSubmit}
        />
      </div>
    </div>
  );
}
